import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import useI18n from './useI18n';

type UseRouterReturn = {
    push: (href: string, options?: PushOptions) => void;
    replace: (href: string, options?: ReplaceOptions) => void;
    updateQueryParams: (query: Record<string, string>) => void;
    refresh: () => void;
    prefetch: (href: string) => void;
    back: () => void;
    forward: () => void;
    pathname: string;
    searchParams: URLSearchParams;
    queryParams: Record<string, string>;
    isActiveURL: (href: string) => boolean;
};

type PushOptions = {
    locale?: string;
    scroll?: boolean;
    query?: Record<string, string>;
    useSameQueryParams?: boolean;
};

type ReplaceOptions = {
    locale?: string;
    scroll?: boolean;
    query?: Record<string, string>;
    useSameQueryParams?: boolean;
};

export default function useNavigation(): UseRouterReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { locale: currentLocale } = useI18n();

    // ----------------------------------------------------------------------------------------------------
    // MARK: Functions
    // ----------------------------------------------------------------------------------------------------
    function push(href: string, { locale = currentLocale, scroll = true, query, useSameQueryParams = false }: PushOptions = {}): void {
        const queryParams = new URLSearchParams(useSameQueryParams ? searchParams : query);
        const queryString = query ? `?${queryParams.toString()}` : '';

        router.push(`/${locale}${href}${queryString}`, { scroll });
    }

    function replace(
        href: string,
        { locale = currentLocale, scroll = true, query, useSameQueryParams = false }: ReplaceOptions = {}
    ): void {
        const queryParams = new URLSearchParams(useSameQueryParams ? searchParams : query);
        const queryString = query ? `?${queryParams.toString()}` : '';

        router.replace(`/${locale}${href}${queryString}`, { scroll });
    }

    function updateQueryParams(query: Record<string, string>): void {
        const queryParams = new URLSearchParams(query);
        const queryString = `?${queryParams.toString()}`;

        router.replace(`${pathname}${queryString}`);
    }

    function back(): void {
        router.back();
    }

    function forward(): void {
        router.forward();
    }

    function refresh(): void {
        router.refresh();
    }

    function prefetch(href: string): void {
        router.prefetch(href);
    }

    function isActiveURL(href: string): boolean {
        const firstSection = pathname.split('/')[2];

        return firstSection === href;
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        push,
        replace,
        updateQueryParams,
        back,
        forward,
        refresh,
        prefetch,
        pathname,
        searchParams,
        queryParams: Object.fromEntries(searchParams.entries()),
        isActiveURL,
    };
}
